// GDPR component for the app
// This component is used to display the GDPR information to the user
// It is displayed when the user clicks on the GDPR button in the footer
// It is also displayed when the user first opens the app
// It is displayed as an alert

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../design-system/primitives/dialog";
import { ActionButton } from "../../design-system/concept/action-button";
import { useEffect, useState } from "react";
import { UserStoragePrefs } from "../../lib/types";
import { NOTHING_AGREED } from "../../lib/consent";
import { Storage } from "@capacitor/storage";

// It is displayed as a modal
const Gdpr = (props: { isOpen: boolean }) => {
    const [fetched, setFetched] = useState(false);
    const [isOpen, setIsOpen] = useState(props.isOpen);
    const [consent, setConsent] = useState<UserStoragePrefs>({});
    const [saved, setSaved] = useState(false);
    useEffect(() => {
        async function getConsent() {
            const stored = await Storage.get({ key: 'consent' });
            if (stored.value) {
                setConsent(JSON.parse(stored.value));
            } else {
                // Nothing, until it is agreed to.
                //
                // This used to open with model_inputs, orig_images and results
                // all true, so a reader who pressed Save without reading —
                // which is what a modal in front of a scan invites — agreed to
                // storing photographs of themselves. GDPR recital 32 is
                // explicit that pre-ticked boxes are not consent, and the
                // dialog's own first line says nothing is stored by default.
                // It now is.
                setConsent(NOTHING_AGREED);
            }
            const saved = await Storage.get({ key: 'saved' });
            if (saved.value) {
                setSaved(saved.value === 'true');
            }
            setFetched(true);
        }
        getConsent();
    }, []);
    useEffect(() => {

        if (fetched) {
            if (!saved) {
                setIsOpen(true);
            }
            setIsOpen(props.isOpen || !saved);
        }
    }, [fetched, props.isOpen]);
    const toggle = (key: keyof UserStoragePrefs) => (checked: boolean) =>
        setConsent({ ...consent, [key]: checked });

    const save = () => {
        setIsOpen(false);
        setSaved(true);
        Storage.set({ key: 'saved', value: 'true' });
        Storage.set({ key: 'consent', value: JSON.stringify(consent) });
    };

    /**
     * Consent, as a dialog rather than an ion-alert.
     *
     * ion-alert took its checkboxes as a data array and handed back only the
     * *checked* values on dismiss, which is why the old save rebuilt the whole
     * object with `Object.fromEntries(values.map(e => [e, true]))` — an
     * unchecked box did not come back as false, it simply vanished. Ordinary
     * inputs and a Save button mean the state is the state.
     */
    const OPTIONS: { key: keyof UserStoragePrefs; label: string; hint: string }[] = [
        {
            key: 'model_inputs',
            label: 'Anonymous metadata',
            hint: 'Measurements derived from the images, with nothing that identifies you.',
        },
        {
            key: 'orig_images',
            label: 'Original photographs',
            hint: 'The photographs themselves. These are of you.',
        },
        {
            key: 'results',
            label: 'Results',
            hint: 'Your measurements and avatars.',
        },
    ];

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && save()}>
            <DialogContent className="sm:max-w-[520px]">
                <DialogHeader>
                    <DialogTitle className="text-base font-semibold tracking-tight">
                        What may we store?
                    </DialogTitle>
                    <DialogDescription className="text-[12.5px] leading-relaxed text-foreground-soft">
                        Nothing is stored by default. Each of these is separate, and
                        you can choose none of them.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col">
                    {OPTIONS.map((o) => (
                        <label
                            key={o.key}
                            className="flex cursor-pointer items-start gap-3 border-b border-divider py-3 last:border-b-0"
                        >
                            <input
                                type="checkbox"
                                checked={Boolean(consent[o.key])}
                                onChange={(e) => toggle(o.key)(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]"
                            />
                            <span>
                                <span className="block text-xs font-medium">{o.label}</span>
                                <span className="block text-[11px] text-muted-foreground">
                                    {o.hint}
                                </span>
                            </span>
                        </label>
                    ))}
                </div>

                <DialogFooter>
                    <ActionButton variant="primary" onClick={save} testId="gdpr-save">
                        Save settings
                    </ActionButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
export default Gdpr;
